/* eslint-disable react-refresh/only-export-components */
/**
 * Auth context — wraps the app, provides useAuth() hook.
 * Integrates with the provider-based auth service.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  authProvider,
  MockAuthProvider,
  type MockAuthSeedUser,
} from "@/services/auth";
import {
  clearApplicationDraft,
  getCurrentSessionOwnerKey,
  restoreApplicationDraft,
  stashApplicationDraft,
} from "@/lib/applicationDraft";
import { clearWizardApplication } from "@/lib/wizardApplicationStore";
import { checkConsentWithApi, clearEConsent } from "@/lib/consent";
import { LO_EMAIL_STORAGE_KEY, normaliseLoEmail } from "@/lib/loEmail";
import {
  setTokenAccessor,
  setTokenRefresher,
  setOnUnauthorized,
} from "@/services/api-client";
import type { RegisterRequest, User } from "@/types/auth";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isMock: boolean;
  /**
   * NP-549 AC-4: `inviteToken` is the co-borrower invitation carried by `/login?invite=…`.
   * LINK-423/424: `otc` is the Lead-invite loan-attach credential carried by `/login?otc=…` —
   * a different credential from a different flow, independent of `inviteToken` and never a
   * substitute for it. Resolves with the loan the API attached, or undefined when nothing
   * was attached — which a lapsed or mismatched credential reaches too, and which is never
   * an error: the borrower is signed in either way.
   */
  login: (
    email: string,
    password: string,
    inviteToken?: string,
    otc?: string,
  ) => Promise<string | undefined>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /**
   * NP-497 — apply freshly-saved profile fields to the signed-in user.
   *
   * `user` is written once at login and then only ever cleared, so saving a profile
   * left the Settings form and the header showing the old name and phone until the
   * borrower logged out and back in. Callers pass what the server returned, so the
   * UI reflects what was actually stored rather than what was typed.
   */
  applyProfileUpdate: (patch: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const LAST_AUTH_OWNER_KEY = "path-auth-last-owner";

function normalizeOwnerKey(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function readLastAuthOwner(): string {
  if (typeof window === "undefined") return "";
  try {
    return normalizeOwnerKey(window.localStorage.getItem(LAST_AUTH_OWNER_KEY));
  } catch {
    return "";
  }
}

function writeLastAuthOwner(ownerKey: string | null | undefined): void {
  if (typeof window === "undefined") return;
  const normalized = normalizeOwnerKey(ownerKey);
  if (!normalized) return;
  try {
    window.localStorage.setItem(LAST_AUTH_OWNER_KEY, normalized);
  } catch {
    // Ignore storage restrictions.
  }
}

function clearCrossAccountState(): void {
  clearApplicationDraft();
  clearWizardApplication();
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("path-active-loan-id");
  } catch {
    // Ignore storage restrictions.
  }
}

export function getDefaultUsers(): MockAuthSeedUser[] {
  return [
    {
      id: "b0000001-0000-0000-0000-000000000001",
      email: "maria.santos@example.com",
      firstName: "Maria",
      lastName: "Santos",
      password: "Password123!",
    },
    {
      // RCA-fix-v2 (2026-06-20): ID MUST match DevMockAuthMiddleware.MockUserMapping
      // which assigns james.chen borrowerId b0000001-0000-0000-0000-000000000002.
      // The v1 fix used b0000002-... which was WRONG — the actual DB PrimaryBorrowerId
      // is b0000001-...-000000000002.  Because DevMockMiddleware now prefers the JWT's
      // own borrower_id claim, the frontend GUID is what gets used for ownership checks.
      // Mismatched GUID → 404 on every GetLoan/PATCH for james.chen.  Fix: align to DB.
      id: "b0000001-0000-0000-0000-000000000002",
      email: "james.chen@example.com",
      firstName: "James",
      lastName: "Chen",
      password: "Password123!",
    },
    // Mapped demo aliases — backend DevMockAuthMiddleware maps these to real borrowers
    {
      id: "b0000001-0000-0000-0000-000000000001",
      email: "test@afncorp.com",
      firstName: "Maria",
      lastName: "Santos",
      password: "Password123!",
    },
    {
      // RCA-fix-v2 (2026-06-20): demo@afncorp.com aliases james.chen → same GUID fix
      id: "b0000001-0000-0000-0000-000000000002",
      email: "demo@afncorp.com",
      firstName: "James",
      lastName: "Chen",
      password: "Password123!",
    },
    // BUG-DEEP-004 fix: add test@path.com as a configured test credential
    {
      id: "b0000001-0000-0000-0000-000000000003",
      email: "test@path.com",
      firstName: "Path",
      lastName: "Tester",
      password: "Password123!",
    },
    // Dedicated E2E test borrower with a unique GUID so no prior loans exist in dev DB.
    // Used by full-loan-creation-fixed.spec.ts to avoid redirect conflicts.
    {
      id: "b9e2e001-0000-0000-0000-e2e000000001",
      email: "e2e-borrower@afncorp.com",
      firstName: "E2E",
      lastName: "TestUser",
      password: "Password123!",
    },
    // Dedicated agent/automation test account — used by Tinker and CI curl verification.
    // Completely isolated GUID so tests never interfere with Maria/James loan data.
    {
      id: "btest001-0000-0000-0000-test00000001",
      email: "tinker-test@example.com",
      firstName: "Tinker",
      lastName: "Test",
      password: "Password123!",
    },
  ];
}

export function isMockAuthProvider(): boolean {
  return (
    "isMock" in authProvider &&
    (authProvider as { isMock?: boolean }).isMock === true
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  // R32a: isLoading starts true so ProtectedRoute shows a spinner instead of
  // redirecting before initializeAuth() has read the session from localStorage.
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authProvider instanceof MockAuthProvider) {
      authProvider.setDefaultUsers(getDefaultUsers());
    }
  }, []);

  // Wire up the api-client token accessor, refresh, and unauthorized handler
  useEffect(() => {
    setTokenAccessor(async () => await authProvider.getToken());
    setTokenRefresher(async () => {
      const token = await authProvider.refreshToken();
      return { accessToken: token.accessToken };
    });
    // Only wire onUnauthorized for real auth providers.
    // MockAuthProvider has isMock=true — in mock mode there's no real backend,
    // so API 401s are expected and should NOT trigger logout.
    const isMock = isMockAuthProvider();
    if (!isMock) {
      setOnUnauthorized(() => {
        authProvider.logout().then(() => setUser(null));
      });
    }
  }, []);

  // Check for existing session on mount.
  useEffect(() => {
    const initializeAuth = async () => {
      // P0 WHITE-PAGE FIX: wrap auth init in a 5-second timeout.
      // If the auth provider never resolves (e.g. localStorage is unavailable),
      // isLoading stays true forever and the app
      // shows a blank spinner. This timeout forces isLoading=false after 5 seconds
      // so the app can render (user will be redirected to login if not authenticated).
      const timeoutMs = 5_000;
      let resolved = false;
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.warn(
            "[Auth] initializeAuth timed out after",
            timeoutMs,
            "ms — forcing isLoading=false",
          );
          setUser(null);
          setIsLoading(false);
        }
      }, timeoutMs);

      try {
        // Check for existing session
        const u = await authProvider.getCurrentUser();
        setUser(u);
      } catch (error) {
        console.error("[Auth] Initialization error:", error);
        setUser(null);
      } finally {
        clearTimeout(timeoutId);
        if (!resolved) {
          resolved = true;
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email: string, password: string, inviteToken?: string, otc?: string) => {
    const previousOwner = readLastAuthOwner() || getCurrentSessionOwnerKey();
    const { user: u, linkedLoanId, loanOfficerEmail } = await authProvider.login(email, password, inviteToken, otc);
    const nextOwner = normalizeOwnerKey(u.email ?? email);

    // LINK-423/424: only present when this login's otc actually attached a loan — update
    // path_lo_email to the invitation's own officer now that it is real, rather than on the
    // strength of the otc merely being presented. Guarded because storage throws outright in
    // restricted privacy mode; theming degrades, the sign-in still works.
    const officer = normaliseLoEmail(loanOfficerEmail);
    if (officer && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(LO_EMAIL_STORAGE_KEY, officer);
      } catch {
        /* storage unavailable — branding degrades, the sign-in still works */
      }
    }

    // New account login must never inherit previous account's local app state.
    if (previousOwner && nextOwner && previousOwner !== nextOwner) {
      clearCrossAccountState();
      // Prevent stale cached data (e.g. loan dropdown) from previous user.
      queryClient.clear();
    }

    // Keep consent strictly per current authenticated account.
    clearEConsent();
    // E-consent login gate (per Rock, 2026-07-23): resolve the consent answer
    // BEFORE exposing the authenticated state. `isAuthenticated` flipping is
    // what triggers every redirect in the app (PublicOnlyRoute etc.) — by
    // awaiting GET /api/v1/borrower/me here, no component can ever observe
    // "authenticated but consent unknown" during a login, so the Dashboard
    // cannot flash. Fails closed (store = 'unconsented') on error/timeout;
    // bounded to 10s inside checkConsentWithApi.
    await checkConsentWithApi(u.email ?? email);
    // NP-430: give the borrower back the wizard work that could not be saved
    // server-side yet (the loan cannot be created until Personal Info supplies
    // a phone, so Income & Assets rows entered first live only in the draft).
    // Scoped to this borrower and only when the live draft is empty.
    restoreApplicationDraft(u.email ?? email);
    writeLastAuthOwner(nextOwner);
    setUser(u);
    // NP-549 AC-9: this login changed which loans the account is on, and the cross-account
    // sweep above did not run for a co-borrower signing in as themselves. Both loan lists
    // were cached before the attach, so the switcher would omit the new loan for the whole
    // cache window.
    if (linkedLoanId) {
      void queryClient.invalidateQueries({ queryKey: ['borrower', 'loans'] });
      void queryClient.invalidateQueries({ queryKey: ['loans', 'incomplete'] });
    }
    return linkedLoanId;
  }, [queryClient]);

  const register = useCallback(async (data: RegisterRequest) => {
    await authProvider.register(data);
  }, []);

  const logout = useCallback(async () => {
    // NP-430: stash before the sweep — the draft is the ONLY copy of anything
    // entered before Personal Info was complete (see applicationDraft.ts).
    stashApplicationDraft(user?.email);
    writeLastAuthOwner(user?.email);
    await authProvider.logout();
    // BUG-11 fix: clear all application draft/progress localStorage keys on logout
    // so the next user session starts completely clean (no stale application data).
    // MockAuthProvider._clearSession() already sweeps all path_/path- keys; calling
    // clearApplicationDraft() here ensures every provider performs the same cleanup
    // without depending on provider internals.
    clearApplicationDraft();
    // Drop cached user-scoped data so next login starts fresh.
    queryClient.clear();
    // Reset the in-memory e-consent store — the next login re-asks the API.
    clearEConsent();
    setUser(null);
  }, [queryClient, user?.email]);

  const resetPassword = useCallback(async (email: string) => {
    await authProvider.resetPassword(email);
  }, []);

  const [hasActiveSession, setHasActiveSession] = useState(false);

  // Check for active session asynchronously
  useEffect(() => {
    let isMounted = true;
    const checkSession = async () => {
      try {
        const token = await authProvider.getToken();
        if (isMounted) {
          setHasActiveSession(Boolean(token));
        }
      } catch {
        if (isMounted) {
          setHasActiveSession(false);
        }
      }
    };
    checkSession();
    return () => {
      isMounted = false;
    };
  }, [user]); // Re-check when user changes

  /**
   * NP-497 — update React state AND the persisted session copy. Doing only the
   * former would make the change vanish on the next refresh, since the user is
   * rehydrated from storage.
   */
  const applyProfileUpdate = useCallback(async (patch: Partial<User>) => {
    const merged = await authProvider.updateStoredUser(patch);
    // Fall back to a local merge when there is nothing stored to update — the
    // signed-in session should still show the new values.
    setUser((current) => (merged ?? (current ? { ...current, ...patch } : current)));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user, // NP-220 fix: Simplified auth check to prevent race conditions
      isLoading,
      isMock: isMockAuthProvider(),
      login,
      register,
      logout,
      resetPassword,
      applyProfileUpdate,
    }),
    [user, isLoading, login, register, logout, resetPassword, applyProfileUpdate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/**
 * NP-529: the signed-in user, or null outside the provider.
 *
 * Non-throwing on purpose. LoanProvider needs to SUBSCRIBE to the authenticated
 * user — not just read it — because AuthProvider takes `children` as props, so a
 * login re-renders AuthProvider while React skips the identical child element.
 * LoanProvider therefore never re-rendered on an account switch and kept serving
 * the previous borrower's loan id. Reading it through context fixes the
 * subscription; `useAuth` cannot be used here because LoanProvider is rendered
 * without an AuthProvider in tests.
 */
export function useAuthUserOptional(): AuthContextValue["user"] | null {
  return useContext(AuthContext)?.user ?? null;
}

export function useAuthMode(): Pick<AuthContextValue, "isMock"> {
  const ctx = useContext(AuthContext);
  return { isMock: ctx?.isMock ?? false };
}
